import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './OnlineOrders.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const today = () => {
  const d = new Date();
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
};

const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtCurrency = (v) => (v != null ? `₹${parseFloat(v).toFixed(2)}` : '—');

const fmtDateTime = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
};

const LC_STATES = ['Placed', 'Confirmed', 'Delivery Assigned', 'Out for Delivery', 'Delivered', 'Undelivered', 'Cancelled'];

const STATE_CLASS = {
  'Placed': 'oo-state--placed',
  'Confirmed': 'oo-state--confirmed',
  'Delivery Assigned': 'oo-state--da',
  'Out for Delivery': 'oo-state--ofd',
  'Delivered': 'oo-state--delivered',
  'Cancelled': 'oo-state--cancelled',
};

const blankSearch = {
  delivery_date: today(),
  order_no: '',
  customer_name: '',
  phone: '',
  order_lc_state: '',
};

/* ── Date navigator ──────────────────────────────────────── */
const DateNav = ({ value, onChange }) => {
  const shift = (delta) => {
    const d = new Date(value + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    onChange([d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-'));
  };
  return (
    <div className="oo-date-nav">
      <button className="oo-date-btn" onClick={() => shift(-1)} title="Previous day">‹</button>
      <input
        type="date"
        className="oo-date-input"
        value={value}
        onChange={e => onChange(e.target.value)}
      />
      <button className="oo-date-btn" onClick={() => shift(1)} title="Next day">›</button>
    </div>
  );
};

/* ── State badge ─────────────────────────────────────────── */
const StateBadge = ({ state }) => (
  <span className={`oo-state-badge ${STATE_CLASS[state] || 'oo-state--default'}`}>
    {state || '—'}
  </span>
);

/* ── Assign Modal ────────────────────────────────────────── */
const AssignModal = ({ order, onClose }) => {
  const { user } = useContext(AuthContext);
  const authHdr = () => ({ headers: { Authorization: `Bearer ${user?.token}` } });

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(new Set());
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/online-admin/supply-partners`,
          { params: { order_id: order.id }, ...authHdr() }
        );
        setPartners(res.data);
      } catch {
        setError('Failed to load supply partners.');
      } finally {
        setLoading(false);
      }
    })();
  }, [order.id]); // eslint-disable-line

  const toggle = async (partner) => {
    setToggling(prev => new Set([...prev, partner.id]));
    setError('');
    try {
      if (partner.is_assigned) {
        await axios.delete(
          `${API_URL}/online-admin/assignments/${order.id}/${partner.id}`,
          authHdr()
        );
      } else {
        await axios.post(
          `${API_URL}/online-admin/assignments`,
          { order_id: order.id, user_id: partner.id },
          authHdr()
        );
      }
      setPartners(prev =>
        prev.map(p => p.id === partner.id ? { ...p, is_assigned: !partner.is_assigned } : p)
      );
    } catch {
      setError('Failed to update assignment. Please try again.');
    } finally {
      setToggling(prev => { const s = new Set(prev); s.delete(partner.id); return s; });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content oo-assign-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Supply Partners &mdash; {order.order_no}</h2>
          <button className="modal-close" onClick={onClose}>&#215;</button>
        </div>

        <div className="oo-assign-body">
          {error && <p className="oo-assign-error">{error}</p>}
          {loading ? (
            <p className="oo-assign-info">Loading&hellip;</p>
          ) : partners.length === 0 ? (
            <p className="oo-assign-info">No active supply partners found.</p>
          ) : (
            <table className="oo-detail-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {partners.map(p => (
                  <tr key={p.id} className={p.is_assigned ? 'oo-assigned-row' : ''}>
                    <td>
                      {p.is_assigned && <span className="oo-assigned-dot">&#9679;</span>}
                      {p.name}
                    </td>
                    <td>{p.phone || '—'}</td>
                    <td>
                      <button
                        className={`oo-toggle-btn ${p.is_assigned ? 'oo-toggle-btn--remove' : 'oo-toggle-btn--add'}`}
                        onClick={() => toggle(p)}
                        disabled={toggling.has(p.id)}
                      >
                        {toggling.has(p.id) ? '…' : p.is_assigned ? 'Remove' : 'Assign'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* ── Cancel Modal ────────────────────────────────────────── */
const CancelModal = ({ order, onClose, onCancelled }) => {
  const { user } = useContext(AuthContext);
  const auth = () => ({ headers: { Authorization: `Bearer ${user?.token}` } });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/pay/refund`, { order_id: order.id }, auth());
      onCancelled(order.id);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Refund failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content oo-cancel-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Cancel Order</h2>
          <button className="modal-close" onClick={onClose}>&#215;</button>
        </div>
        <div className="oo-cancel-body">
          <p className="oo-cancel-msg">
            Are you sure you want to cancel order <strong>{order.order_no}</strong>?
          </p>
          <div className="oo-cancel-refund">
            <div style={{ marginBottom: '4px', fontWeight: 600 }}>The following refunds will be issued:</div>
            {parseFloat(order.razorpay_amount || 0) > 0 && (
              <div>• Razorpay refund: <strong>{fmtCurrency(order.razorpay_amount)}</strong></div>
            )}
            {parseFloat(order.wallet_cash_amount || 0) > 0 && (
              <div>• Wallet cash refund: <strong>{fmtCurrency(order.wallet_cash_amount)}</strong></div>
            )}
            {parseFloat(order.wallet_points_amount || 0) > 0 && (
              <div>• Reward points refund: <strong>{parseFloat(order.wallet_points_amount).toFixed(2)} pts</strong></div>
            )}
            {!parseFloat(order.razorpay_amount || 0) && !parseFloat(order.wallet_cash_amount || 0) && !parseFloat(order.wallet_points_amount || 0) && (
              <div>A refund of <strong>{fmtCurrency(order.order_amount)}</strong> will be issued.</div>
            )}
          </div>
          {error && <p className="oo-cancel-error">{error}</p>}
        </div>
        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose} disabled={loading}>Back</button>
          <button className="oo-cancel-confirm-btn" onClick={handleConfirm} disabled={loading}>
            {loading ? 'Processing…' : 'Confirm Cancellation'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Detail Modal ────────────────────────────────────────── */
const DetailModal = ({ detail, onClose }) => {
  if (!detail) return null;
  const { order, lines, assignments, lifecycle = [] } = detail;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content oo-modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Order {order.order_no}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="oo-detail-body">

          {/* Customer */}
          <section className="oo-detail-section">
            <h3 className="oo-detail-section-title">Customer</h3>
            <div className="oo-detail-grid3">
              <div className="oo-kv"><span className="oo-k">Name</span><span className="oo-v">{order.customer_name || '—'}</span></div>
              <div className="oo-kv"><span className="oo-k">Phone</span><span className="oo-v">{order.phone || '—'}</span></div>
              <div className="oo-kv oo-kv--full"><span className="oo-k">Address</span><span className="oo-v">{order.address || '—'}</span></div>
            </div>
          </section>

          {/* Order Summary */}
          <section className="oo-detail-section">
            <h3 className="oo-detail-section-title">Order Summary</h3>
            <div className="oo-detail-grid3">
              <div className="oo-kv"><span className="oo-k">Order No</span><span className="oo-v">{order.order_no}</span></div>
              <div className="oo-kv"><span className="oo-k">Order Date</span><span className="oo-v">{fmtDate(order.order_date)}</span></div>
              <div className="oo-kv"><span className="oo-k">Status</span><span className="oo-v"><StateBadge state={order.order_lc_state} /></span></div>
              <div className="oo-kv"><span className="oo-k">Delivery Date</span><span className="oo-v">{fmtDate(order.delivery_date)}</span></div>
              <div className="oo-kv"><span className="oo-k">Delivery Window</span><span className="oo-v">{order.delivery_window || '—'}</span></div>
              <div className="oo-kv"><span className="oo-k">Order State</span><span className="oo-v">{order.order_state || '—'}</span></div>
              <div className="oo-kv"><span className="oo-k">Order Amount</span><span className="oo-v">{fmtCurrency(order.order_amount)}</span></div>
              <div className="oo-kv"><span className="oo-k">Discount</span><span className="oo-v">{fmtCurrency(order.discount)}</span></div>
              <div className="oo-kv"><span className="oo-k">Receipt Amount</span><span className="oo-v">{fmtCurrency(order.receipt_amount)}</span></div>
            </div>
          </section>

          {/* Order Lines */}
          <section className="oo-detail-section">
            <h3 className="oo-detail-section-title">Items ({lines.length})</h3>
            {lines.length === 0 ? (
              <p className="oo-detail-empty">No items.</p>
            ) : (
              <table className="oo-detail-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th className="oo-th-num">Qty</th>
                    <th className="oo-th-num">Unit Price</th>
                    <th className="oo-th-num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map(l => (
                    <React.Fragment key={l.id}>
                      <tr className={l.is_pack ? 'oo-pack-line' : ''}>
                        <td>{l.product_name || `Product #${l.product_id}`}</td>
                        <td className="oo-td-num">{l.qty}</td>
                        <td className="oo-td-num">{fmtCurrency(l.unit_price)}</td>
                        <td className="oo-td-num">{fmtCurrency(l.total_price)}</td>
                      </tr>
                      {l.is_pack && l.selectionGroups && l.selectionGroups.flatMap((g, gi) =>
                        g.items.map((it, ii) => (
                          <tr key={`${l.id}-g${gi}-i${ii}`} className="oo-pack-sel-row">
                            <td colSpan={4} className="oo-pack-sel-cell">
                              <span className="oo-pack-sel-group">{g.name}:</span>{' '}
                              {it.product_name}{' '}
                              <span className="oo-pack-sel-qty">×{it.qty}</span>
                            </td>
                          </tr>
                        ))
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* Assignments */}
          {assignments.length > 0 && (
            <section className="oo-detail-section">
              <h3 className="oo-detail-section-title">Assigned To</h3>
              <div className="oo-assignments">
                {assignments.map(a => (
                  <div key={a.id} className="oo-assignment-chip">
                    <span className="oo-assign-role">{a.order_role}</span>
                    <span className="oo-assign-name">{a.user_name}</span>
                    {a.user_phone && <span className="oo-assign-phone">{a.user_phone}</span>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Lifecycle */}
          <section className="oo-detail-section">
            <h3 className="oo-detail-section-title">Order History</h3>
            {lifecycle.length === 0 ? (
              <p className="oo-detail-empty">No history records.</p>
            ) : (
              <table className="oo-detail-table">
                <thead>
                  <tr>
                    <th>Date &amp; Time</th>
                    <th>Event</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Role</th>
                    <th>Notes</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {lifecycle.map(lc => (
                    <tr key={lc.id}>
                      <td className="oo-lc-dt">{fmtDateTime(lc.created_date)}</td>
                      <td>{lc.event_type}</td>
                      <td>{lc.from_state || '—'}</td>
                      <td>{lc.assigned_user_name || lc.to_state || '—'}</td>
                      <td>{lc.order_role || '—'}</td>
                      <td>{lc.notes || '—'}</td>
                      <td>{lc.created_by_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* ── Main Page ───────────────────────────────────────────── */
const OnlineOrders = () => {
  const { user } = useContext(AuthContext);
  const auth = () => ({ headers: { Authorization: `Bearer ${user?.token}` } });

  const [tab, setTab] = useState('overview');
  const [search, setSearch] = useState(blankSearch);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [assignOrder, setAssignOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [slotItems, setSlotItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [canAssign, setCanAssign] = useState(false);
  const [canDeliver, setCanDeliver] = useState(false);
  const [delivering, setDelivering] = useState(new Set());
  const [canCancel, setCanCancel] = useState(false);
  const [cancelOrder, setCancelOrder] = useState(null);
  const [overviewData, setOverviewData] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const filterParams = (params) =>
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== ''));

  const fetchOrders = useCallback(async (params) => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${API_URL}/online-admin/orders`, {
        params: filterParams(params),
        ...auth(),
      });
      setOrders(res.data);
    } catch {
      setError('Failed to load orders.');
    } finally {
      setLoading(false);
    }
  }, [user?.token]);

  const fetchOverview = useCallback(async () => {
    try {
      setOverviewLoading(true);
      const res = await axios.get(`${API_URL}/online-admin/overview`, auth());
      setOverviewData(res.data);
    } catch {
      setOverviewData([]);
    } finally {
      setOverviewLoading(false);
    }
  }, [user?.token]); // eslint-disable-line

  const fetchItems = useCallback(async (params) => {
    try {
      setItemsLoading(true);
      const fp = filterParams(params);
      const [r1, r2] = await Promise.all([
        axios.get(`${API_URL}/online-admin/order-items`, { params: fp, ...auth() }),
        axios.get(`${API_URL}/online-admin/order-items-by-slot`, { params: fp, ...auth() }),
      ]);
      setItems(r1.data);
      setSlotItems(r2.data);
    } catch {
      setItems([]);
      setSlotItems([]);
    } finally {
      setItemsLoading(false);
    }
  }, [user?.token]);

  useEffect(() => { fetchOverview(); fetchOrders(search); }, []);

  useEffect(() => {
    axios.post(
      `${API_URL}/authRoutes/permissions/check`,
      { permissionName: 'online.orders.read' },
      auth()
    ).then(res => setCanAssign(res.data.hasPermission)).catch(() => {});

    axios.post(
      `${API_URL}/authRoutes/permissions/check`,
      { permissionName: 'online.orders.myorder' },
      auth()
    ).then(res => setCanDeliver(res.data.hasPermission)).catch(() => {});

    axios.post(
      `${API_URL}/authRoutes/permissions/check`,
      { permissionName: 'online.orders.cancel' },
      auth()
    ).then(res => setCanCancel(res.data.hasPermission)).catch(() => {});
  }, [user?.token]); // eslint-disable-line

  const toggleDelivered = async (order) => {
    const newLcState = order.order_lc_state === 'Delivered' ? 'Out for Delivery' : 'Delivered';
    const newOrderState = newLcState === 'Delivered' ? 'Fulfilled' : 'Draft';
    setOrders(prev => prev.map(o =>
      o.id === order.id ? { ...o, order_lc_state: newLcState, order_state: newOrderState } : o
    ));
    setDelivering(prev => new Set([...prev, order.id]));
    try {
      await axios.put(`${API_URL}/online-admin/orders/${order.id}/lc-state`, { state: newLcState }, auth());
    } catch (err) {
      setOrders(prev => prev.map(o =>
        o.id === order.id ? { ...o, order_lc_state: order.order_lc_state, order_state: order.order_state } : o
      ));
      const msg = err.response?.data?.error || 'Failed to update order state.';
      alert(msg);
    } finally {
      setDelivering(prev => { const s = new Set(prev); s.delete(order.id); return s; });
    }
  };

  const handleSearchField = (name, value) => {
    const updated = { ...search, [name]: value };
    setSearch(updated);
    fetchOrders(updated);
    if (tab === 'items') fetchItems(updated);
  };

  const handleTabChange = (newTab) => {
    setTab(newTab);
    if (newTab === 'overview') fetchOverview();
    if (newTab === 'items') fetchItems(search);
  };

  const handleReset = () => {
    const s = blankSearch;
    setSearch(s);
    fetchOrders(s);
    if (tab === 'items') fetchItems(s);
  };

  const openDetail = async (order) => {
    setDetailLoading(true);
    setDetail({ order, lines: [], assignments: [] });
    try {
      const res = await axios.get(`${API_URL}/online-admin/order-detail/${order.id}`, auth());
      setDetail(res.data);
    } catch {
      setDetail({ order, lines: [], assignments: [] });
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="oo-page">

      {/* Header */}
      <div className="oo-header">
        <div>
          <h1 className="oo-title">Online Orders</h1>
        </div>
        <span className="oo-count">{orders.length} orders</span>
      </div>

      {/* Tabs */}
      <div className="oo-tabs">
        <button className={`oo-tab ${tab === 'overview' ? 'oo-tab--active' : ''}`} onClick={() => handleTabChange('overview')}>
          Overview
        </button>
        <button className={`oo-tab ${tab === 'orders' ? 'oo-tab--active' : ''}`} onClick={() => handleTabChange('orders')}>
          Orders
        </button>
        <button className={`oo-tab ${tab === 'items' ? 'oo-tab--active' : ''}`} onClick={() => handleTabChange('items')}>
          Item Details
        </button>
      </div>

      {tab === 'overview' && (() => {
        const dateRange = Array.from({ length: 9 }, (_, i) => {
          const d = new Date();
          d.setDate(d.getDate() + i);
          return [
            d.getFullYear(),
            String(d.getMonth() + 1).padStart(2, '0'),
            String(d.getDate()).padStart(2, '0'),
          ].join('-');
        });

        const byDate = overviewData.reduce((acc, row) => {
          const key = row.delivery_date?.slice(0, 10);
          if (!acc[key]) acc[key] = [];
          acc[key].push(row);
          return acc;
        }, {});

        const cardLabel = (iso, i) => {
          if (i === 0) return 'Today';
          if (i === 1) return 'Tomorrow';
          const d = new Date(iso + 'T00:00:00');
          return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
        };

        return (
          <div className="oo-overview-grid">
            {dateRange.map((iso, i) => {
              const items = byDate[iso] || [];
              const totalUnits = items.reduce((s, r) => s + parseInt(r.total_qty, 10), 0);
              return (
                <div key={iso} className="oo-overview-card">
                  <div className="oo-overview-card-header">
                    <span className="oo-overview-date">{cardLabel(iso, i)}</span>
                    <button
                      className="oo-overview-link"
                      onClick={() => { handleSearchField('delivery_date', iso); setTab('orders'); }}
                    >
                      Order details
                    </button>
                    {!overviewLoading && (
                      <span className="oo-overview-badge">
                        {items.length > 0 ? `${totalUnits} units` : 'No orders'}
                      </span>
                    )}
                  </div>
                  <div className="oo-overview-card-body">
                    {overviewLoading ? (
                      <p className="oo-overview-empty">Loading…</p>
                    ) : items.length === 0 ? (
                      <p className="oo-overview-empty">No orders</p>
                    ) : (
                      <table className="oo-overview-table">
                        <tbody>
                          {items.map((item, j) => (
                            <tr key={j}>
                              <td className="oo-ov-name">{item.product_name}</td>
                              <td className="oo-ov-qty">{item.total_qty}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {tab === 'orders' && (
        <>
          {/* Search Bar */}
          <div className="oo-search-bar">
            <div className="oo-search-field">
              <label className="oo-search-label">Delivery Date</label>
              <DateNav
                value={search.delivery_date}
                onChange={v => handleSearchField('delivery_date', v)}
              />
            </div>
            <div className="oo-search-field">
              <label className="oo-search-label">Order No</label>
              <input
                className="oo-search-input"
                value={search.order_no}
                onChange={e => handleSearchField('order_no', e.target.value)}
                placeholder="Order No"
              />
            </div>
            <div className="oo-search-field">
              <label className="oo-search-label">Customer Name</label>
              <input
                className="oo-search-input"
                value={search.customer_name}
                onChange={e => handleSearchField('customer_name', e.target.value)}
                placeholder="Customer name"
              />
            </div>
            <div className="oo-search-field">
              <label className="oo-search-label">Phone</label>
              <input
                className="oo-search-input"
                value={search.phone}
                onChange={e => handleSearchField('phone', e.target.value)}
                placeholder="Phone"
              />
            </div>
            <div className="oo-search-field">
              <label className="oo-search-label">Status</label>
              <select
                className="oo-search-select"
                value={search.order_lc_state}
                onChange={e => handleSearchField('order_lc_state', e.target.value)}
              >
                <option value="">All Statuses</option>
                {LC_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="oo-search-actions">
              <button className="pd-btn-reset" onClick={handleReset}>Reset</button>
            </div>
          </div>

          {/* Alert */}
          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠</span>{error}
              <button className="alert-close" onClick={() => setError('')}>×</button>
            </div>
          )}

          {/* Table */}
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Order No</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Order Date</th>
                  <th>Delivery Date</th>
                  <th>Delivery Window</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="no-data">Loading…</td></tr>
                ) : orders.length === 0 ? (
                  <tr><td colSpan={8} className="no-data">No orders found.</td></tr>
                ) : orders.map(o => (
                  <tr key={o.id}>
                    <td className="oo-order-no">{o.order_no}</td>
                    <td>{o.customer_name}</td>
                    <td>{o.phone || <span style={{ color: '#bbb' }}>—</span>}</td>
                    <td>{fmtDate(o.order_date)}</td>
                    <td>{fmtDate(o.delivery_date)}</td>
                    <td>{o.delivery_window || <span style={{ color: '#bbb' }}>—</span>}</td>
                    <td><StateBadge state={o.order_lc_state} /></td>
                    <td className="oo-action-cell">
                      <button
                        className="oo-detail-btn"
                        onClick={() => openDetail(o)}
                        title="View details"
                      >
                        {detailLoading && detail?.order?.id === o.id ? '…' : '🔍'}
                      </button>
                      {canAssign && (
                        <button
                          className="oo-assign-btn"
                          onClick={() => setAssignOrder(o)}
                          title="Assign supply partners"
                        >
                          &#128101;
                        </button>
                      )}
                      {canDeliver && (
                        <button
                          className={`oo-deliver-btn${o.order_lc_state === 'Delivered' ? ' oo-deliver-btn--done' : ''}${!o.has_assignment ? ' oo-deliver-btn--disabled' : ''}`}
                          onClick={() => toggleDelivered(o)}
                          disabled={delivering.has(o.id) || !o.has_assignment}
                          title={!o.has_assignment ? 'No delivery partner assigned' : o.order_lc_state === 'Delivered' ? 'Revert to Out for Delivery' : 'Mark as Delivered'}
                        >
                          {delivering.has(o.id) ? '…' : o.order_lc_state === 'Delivered' ? '↩' : '🚚'}
                        </button>
                      )}
                      {canCancel && (o.online_payment_id || parseFloat(o.wallet_cash_amount || 0) > 0 || parseFloat(o.wallet_points_amount || 0) > 0) && o.order_lc_state !== 'Cancelled' && (
                        <button
                          className="oo-cancel-btn"
                          onClick={() => setCancelOrder(o)}
                          title="Cancel order &amp; refund"
                        >
                          &#10006;
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'items' && (() => {
        const slots = [...new Set(slotItems.map(r => r.delivery_window))];
        return (
          <div className="oo-items-tab">
            {/* Overall summary */}
            <div className="oo-slot-block">
              <div className="oo-slot-label">Items for Today</div>
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th className="oo-th-num">Total Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {itemsLoading ? (
                    <tr><td colSpan={2} className="no-data">Loading…</td></tr>
                  ) : items.length === 0 ? (
                    <tr><td colSpan={2} className="no-data">No items found.</td></tr>
                  ) : items.map((item, i) => (
                    <tr key={i}>
                      <td>{item.product_name}</td>
                      <td className="oo-td-num">{item.total_qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Items by Delivery Slot */}
            {!itemsLoading && slots.length > 0 && (
              <div className="oo-slot-sections">
                <h3 className="oo-slot-heading">Items by Delivery Slot</h3>
                {slots.map(slot => {
                  const rows = slotItems.filter(r => r.delivery_window === slot);
                  return (
                    <div key={slot} className="oo-slot-block">
                      <div className="oo-slot-label">{slot}</div>
                      <table className="users-table">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th className="oo-th-num">Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r, i) => (
                            <tr key={i}>
                              <td>{r.product_name}</td>
                              <td className="oo-td-num">{r.total_qty}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* Detail Modal */}
      {detail && !detailLoading && (
        <DetailModal detail={detail} onClose={() => setDetail(null)} />
      )}

      {/* Assign Modal */}
      {assignOrder && (
        <AssignModal order={assignOrder} onClose={() => { setAssignOrder(null); fetchOrders(search); }} />
      )}

      {/* Cancel Modal */}
      {cancelOrder && (
        <CancelModal
          order={cancelOrder}
          onClose={() => setCancelOrder(null)}
          onCancelled={(id) =>
            setOrders(prev => prev.map(o =>
              o.id === id ? { ...o, order_lc_state: 'Cancelled', order_state: 'Cancelled' } : o
            ))
          }
        />
      )}
    </div>
  );
};

export default OnlineOrders;
